import nxModule from 'nxModule';
import {ILocationService} from 'angular';
import {NxRouteService} from 'routes/NxRouteService';
import systemPropertyService from 'system/systemPropertyService';
import {HttpService} from 'shared/utils/httpService';
import {CommandService} from 'shared/utils/command/command.types';
import {Confirmation} from 'shared/common/confirmation.types';
import {CustomerCache} from 'components/service/customer.cache.types';
import {Account} from 'components/service/product.types';
import ParameterizedLocalCache from 'shared/utils/parameterized-local-cache';

import templateUrl from './edit-account-sweep.template.html';

interface SweepConfig {
  id: number;
  accountId: number;
  targetId: number;
  enabled: boolean;
  sweepInterval: number;
  retainMaintainingBalance: boolean;
}

interface SweepCommandInput {
  productId: number;
  enabled: boolean;
  sweepInterval: number | null;
  targetId: number | null;
  retainMaintainingBalance: boolean;
}

interface IntervalOption {
  label: string;
  value: number;
}

interface TargetResult {
  id: number;
  productNumber: string;
  customerName: string;
}

interface Ownership {
  ownershipType: string;
  effectiveName: string;
}

interface AccountSearchResult {
  id: number;
  productNumber: string;
  ownership?: Ownership[];
}

interface AccountSearchResponse {
  result: AccountSearchResult[];
}

export class EditAccountSweepController {
  sweepInput: SweepCommandInput;
  currentSweep: Partial<SweepConfig>;
  currentTargetDisplay: string | null;
  targetProductNumberInput: string;
  targetResults: TargetResult[];
  targetError: string | null;
  productNumberMask: string | null;
  intervalOptions: IntervalOption[];
  account!: Account;

  private readonly customerId: string;
  private readonly accountId: string;

  constructor(
    $route: NxRouteService,
    private $location: ILocationService,
    private http: HttpService,
    private command: CommandService,
    private confirmation: Confirmation,
    private customerCache: CustomerCache,
    private sweepCache: ParameterizedLocalCache<unknown, SweepConfig>
  ) {
    this.customerId = $route.current.params['customerId'] as string;
    this.accountId = $route.current.params['accountId'] as string;

    this.sweepInput = {
      productId: Number(this.accountId),
      enabled: false,
      sweepInterval: null,
      targetId: null,
      retainMaintainingBalance: false
    };
    this.currentSweep = {};
    this.currentTargetDisplay = null;
    this.targetProductNumberInput = '';
    this.targetResults = [];
    this.targetError = null;

    this.productNumberMask = systemPropertyService.getProperty('PRODUCT_NUMBER_MASK');

    const intervalsProperty = systemPropertyService.getProperty('AUTO_SWEEP_INTERVALS') || '';
    this.intervalOptions = intervalsProperty.split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .map(v => {
        const num = Number(v);
        return {label: num + ' minutes', value: num};
      });
  }

  async $onInit(): Promise<void> {
    if (systemPropertyService.getProperty('AUTO_SWEEP_FACILITY_ENABLED') !== 'TRUE') {
      this.redirect();
      return;
    }

    const accounts = await this.customerCache.depositAccounts(this.customerId).toPromise();
    this.account = accounts.find((a: Account) => a.id === Number(this.accountId))!;

    try {
      const sweep = await this.sweepCache.withParam(this.accountId).toPromise();
      if (sweep) {
        this.currentSweep = sweep;
        this.sweepInput.enabled = sweep.enabled;
        this.sweepInput.sweepInterval = sweep.sweepInterval;
        this.sweepInput.retainMaintainingBalance = sweep.retainMaintainingBalance;

        if (sweep.targetId) {
          const target = await this.http.get<AccountSearchResult>(`/products/accounts/${sweep.targetId}`).toPromise();
          if (target) {
            const ownerEntry = (target.ownership || []).find(o => o.ownershipType === 'OWNER');
            const ownerName = ownerEntry ? ownerEntry.effectiveName : '';
            this.currentTargetDisplay = target.productNumber + (ownerName ? ' - ' + ownerName : '');
            this.targetProductNumberInput = target.productNumber;
            this.sweepInput.targetId = sweep.targetId;
          }
        }
      }
    } catch {
      this.currentSweep = {enabled: false};
    }
  }

  async searchTarget(): Promise<void> {
    this.targetResults = [];
    this.targetError = null;
    this.sweepInput.targetId = null;

    const input = this.targetProductNumberInput;
    if (!input || input.length < 3) return;

    const data = await this.http.post<AccountSearchResponse>('/products/accounts/search', {
      productNumber: [input],
      productStatus: ['ACTIVE', 'PENDING'],
      includeOwnership: true,
      pageSize: 5
    }).toPromise();

    const results = data.result || [];
    this.targetResults = results
      .filter(r => r.id !== Number(this.accountId))
      .map(r => {
        const ownerEntry = (r.ownership || []).find(o => o.ownershipType === 'OWNER');
        const ownerName = ownerEntry ? ownerEntry.effectiveName : '';
        return {
          id: r.id,
          productNumber: r.productNumber,
          customerName: ownerName
        };
      });

    if (results.length > 0 && this.targetResults.length === 0) {
      this.targetError = 'Target account cannot be the same as the source account.';
    }
  }

  selectTarget(result: TargetResult): void {
    this.sweepInput.targetId = result.id;
    this.targetProductNumberInput = result.productNumber;
    this.targetResults = [];
    this.targetError = null;
  }

  redirect(): void {
    this.$location.path(`/customer/${this.customerId}/accounts/${this.accountId}`);
  }

  redirectBack(): void {
    this.confirmation('Do you want to cancel? Canceling will discard all changes.', () => this.redirect());
  }

  async update(): Promise<void> {
    const productNumber = this.account ? this.account.productNumber : this.accountId;
    this.confirmation(`Do you want to update the Automatic balance sweep configuration for ${productNumber} account?`, async () => {
      await this.command.execute('ConfigureAutoBalanceSweep', {
        productId: Number(this.accountId),
        enabled: this.sweepInput.enabled,
        sweepInterval: this.sweepInput.sweepInterval,
        targetId: this.sweepInput.targetId,
        retainMaintainingBalance: this.sweepInput.retainMaintainingBalance
      }).toPromise();
      this.customerCache.depositAccounts(this.customerId).refetch();
      this.sweepCache.withParam(this.accountId).refetch();
      this.redirect();
    });
  }
}

nxModule.component('customerAccountEditSweep', {
  templateUrl,
  controller: EditAccountSweepController
});
